import { useReducer, type Dispatch } from 'react';

import { cancelReducer } from './cancel-machine.js';
import { INITIAL_CANCEL_STATE, type CancelState } from './cancel-state.type.js';
import type { CancelAction } from './cancel-action.type.js';

/**
 * Rückgabewert des Cancel-Machine-Hooks: aktueller State + Dispatch.
 *
 * Bewusst kein Methoden-Set — Komponenten kennen die diskriminierte Action-
 * Union und triggern direkt, ohne dass der Hook zwischen Verb und Reducer-
 * Action übersetzen müsste.
 */
export interface CancelMachine {
  readonly state: CancelState;
  readonly dispatch: Dispatch<CancelAction>;
}

/**
 * Optionen für `useCancelMachine`.
 *
 * `initialState` ist optional und dient ausschließlich Tests sowie dem Dev-
 * Screen-Switcher, damit ein Reload nicht auf `START` zurückwirft, wenn der/die
 * Tester:in gerade einen Error-Screen ansieht.
 */
interface UseCancelMachineOptions {
  readonly initialState?: CancelState;
}

/**
 * React-Hook, der den `cancelReducer` über `useReducer` mountet und
 * `{ state, dispatch }` zurückgibt.
 *
 * Side-Effects (API, Crypto, Pinning) gehören explizit nicht hierher —
 * dieser Hook ist ein dünner `useReducer`-Wrapper. Effekte werden in eigenen
 * Custom-Hooks (DEV-012/013/015) implementiert und dispatchen ihrerseits in
 * diesen Reducer.
 *
 * @param options Optionaler Override für den initialen State.
 */
export function useCancelMachine(options: UseCancelMachineOptions = {}): CancelMachine {
  const initial = options.initialState ?? INITIAL_CANCEL_STATE;
  const [state, dispatch] = useReducer(cancelReducer, initial);
  return { state, dispatch };
}
